// Where the markers came from, and where they disagree with the sensors.
//
// Two backend views, side by side with the Milestones table:
//   * find_marker_discrepancies — recorded vs estimated more than N s apart
//   * review_milestone_confidence — effective confidence below a bar
//
// Both are diagnostics, not verdicts. The point is to make "this first
// crack is a recipe stamp the detector disagrees with by 90 s" visible
// next to the number it qualifies, with the fix one click away.

import { Link } from "react-router-dom";
import type {
  Envelope,
  MarkerDiscrepanciesData,
  MilestoneConfidenceData,
} from "../types";
import { milestoneName, sourceLabel } from "../chart";
import { formatConfidence, formatTime } from "../format";

export interface MarkerProvenanceProps {
  roastId: string;
  discrepancies: Envelope<MarkerDiscrepanciesData> | null;
  lowConfidence: Envelope<MilestoneConfidenceData> | null;
  error?: unknown;
}

export function MarkerProvenance({
  roastId,
  discrepancies,
  lowConfidence,
  error,
}: MarkerProvenanceProps) {
  const hits = discrepancies?.data.discrepancies ?? [];
  const low = lowConfidence?.data.low_confidence ?? [];
  const clean = hits.length === 0 && low.length === 0;

  return (
    <aside className="provenance" aria-labelledby="provenance-title">
      <div className="row" style={{ alignItems: "baseline" }}>
        <h3 id="provenance-title" style={{ margin: 0 }}>
          Marker provenance
        </h3>
        <Link
          to={`/roasts/${encodeURIComponent(roastId)}/correct`}
          className="small right"
        >
          Correct a milestone →
        </Link>
      </div>

      {error != null && (
        <p className="small muted">Provenance checks unavailable: {String((error as Error)?.message ?? error)}</p>
      )}

      {discrepancies && (
        <p className="small muted" style={{ margin: "6px 0 2px" }}>
          {discrepancies.text}
        </p>
      )}
      {hits.length > 0 && (
        <table className="compact">
          <thead>
            <tr>
              <th>Milestone</th>
              <th>Recorded</th>
              <th>Estimated</th>
              <th>Δ</th>
              <th>In use</th>
            </tr>
          </thead>
          <tbody>
            {hits.map((h) => {
              const cf = formatConfidence(h.confidence);
              return (
                <tr key={h.milestone_type}>
                  <td>{milestoneName(h.milestone_type)}</td>
                  <td className="mono">
                    {formatTime(h.recorded_time_s)}
                    <div className="small muted">{sourceLabel(h.recorded_source)}</div>
                  </td>
                  <td className="mono">
                    {formatTime(h.estimated_time_s)}
                    <div className="small muted">
                      detector {formatConfidence(h.detector_confidence).label}
                    </div>
                  </td>
                  <td className="mono">{formatTime(h.delta_s)}</td>
                  <td className="small">
                    {sourceLabel(h.effective_source)}{" "}
                    <span className={`badge ${cf.cls}`}>{cf.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {lowConfidence && (
        <p className="small muted" style={{ margin: "10px 0 2px" }}>
          {lowConfidence.text}
        </p>
      )}
      {low.length > 0 && (
        <ul className="plain small">
          {low.map((m) => {
            const cf = formatConfidence(m.confidence);
            return (
              <li key={m.milestone_type} style={{ marginBottom: 6 }}>
                <strong>{milestoneName(m.milestone_type)}</strong>{" "}
                <span className={`badge ${cf.cls}`}>{cf.label}</span>{" "}
                <span className="muted">
                  {sourceLabel(m.effective_source)}
                  {m.effective_source === "estimated" && m.estimation_method
                    ? ` via ${m.estimation_method}`
                    : ""}
                  {m.recorded_source !== "unknown" &&
                    m.effective_source !== m.recorded_source &&
                    ` · marker was ${sourceLabel(m.recorded_source)}`}
                </span>
                {m.reason_codes.length > 0 && (
                  <div style={{ marginTop: 2 }}>
                    {m.reason_codes.map((c) => (
                      <span className="badge" key={c}>
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {clean && discrepancies && lowConfidence && (
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          No marker disagrees with the sensors by more than the tolerance,
          and no milestone is below the confidence bar. That says the
          values agree — not that any of them was observed by a human.
        </p>
      )}
    </aside>
  );
}
